import { useState, useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sunrise,
  Sunset,
  Plus,
  X,
  TrendingUp,
  MapPin,
  Calendar,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { format, addDays } from "date-fns";
import { useLocations } from "@/hooks/useLocations";
import { useLocationForecast, type LocationForecast } from "@/hooks/useLocationForecast";
import { useAuth } from "@/hooks/useAuth";
import { usePreferences } from "@/hooks/usePreferences";
import {
  getQualityGradient,
  formatQualityScore,
  normalizeQualityCategory,
  getQualityIcon,
} from "@/lib/qualityScore";
import { formatTemperature } from "@/lib/units";
import { formatTimeInTimezone, getUserTimezone } from "@/lib/timezone";
import { Link } from "wouter";

const MAX_LOCATIONS = 3;

interface ComparisonLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

interface ComparisonCardProps {
  location: ComparisonLocation;
  forecast: LocationForecast | null;
  isLoading: boolean;
  error: Error | null;
  onRemove: () => void;
  temperatureUnit: "celsius" | "fahrenheit";
  timeFormat: "12h" | "24h";
  timezone: string;
  bestSunrise?: boolean;
  bestSunset?: boolean;
}

function ComparisonCard({
  location,
  forecast,
  isLoading,
  error,
  onRemove,
  temperatureUnit,
  timeFormat,
  timezone,
  bestSunrise,
  bestSunset,
}: ComparisonCardProps) {
  const formatTime = (isoString: string | null) => {
    if (!isoString) return "--:--";
    return formatTimeInTimezone(isoString, timezone, timeFormat);
  };

  if (isLoading) {
    return (
      <Card className="relative min-h-[400px] flex items-center justify-center">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-6 w-6"
          onClick={onRemove}
          aria-label={`Remove ${location.name}`}
        >
          <X className="h-4 w-4" />
        </Button>
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </Card>
    );
  }

  if (error || !forecast) {
    return (
      <Card className="relative min-h-[400px]">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-6 w-6 z-10"
          onClick={onRemove}
          aria-label={`Remove ${location.name}`}
        >
          <X className="h-4 w-4" />
        </Button>
        <CardHeader>
          <CardTitle className="text-lg truncate pr-8">{location.name}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center min-h-[300px] text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Unable to load forecast</p>
          <p className="text-sm text-muted-foreground mt-1">
            {error?.message || "Please try again later"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-6 w-6 z-10"
        onClick={onRemove}
        aria-label={`Remove ${location.name}`}
      >
        <X className="h-4 w-4" />
      </Button>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg truncate pr-8 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
          {location.name}
        </CardTitle>
        {forecast.temperature && (
          <p className="text-sm text-muted-foreground">
            {formatTemperature(forecast.temperature, temperatureUnit)}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Sunrise Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sunrise className="h-4 w-4 text-amber-500" />
              <span className="font-medium text-sm">Sunrise</span>
              {bestSunrise && (
                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Best
                </Badge>
              )}
            </div>
          </div>
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 p-3 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-lg font-semibold">
                {formatTime(forecast.sunriseTime)}
              </span>
              <Badge className={`${getQualityGradient(forecast.sunriseCategory)} text-white text-xs`}>
                {getQualityIcon(forecast.sunriseCategory)}{" "}
                {normalizeQualityCategory(forecast.sunriseCategory)}
              </Badge>
            </div>
            <div className="text-2xl font-bold text-center py-2">
              {formatQualityScore(forecast.sunriseQuality)}
            </div>
          </div>
        </div>

        <Separator />

        {/* Sunset Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sunset className="h-4 w-4 text-orange-500" />
              <span className="font-medium text-sm">Sunset</span>
              {bestSunset && (
                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Best
                </Badge>
              )}
            </div>
          </div>
          <div className="bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-950/30 dark:to-pink-950/30 p-3 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-lg font-semibold">
                {formatTime(forecast.sunsetTime)}
              </span>
              <Badge className={`${getQualityGradient(forecast.sunsetCategory)} text-white text-xs`}>
                {getQualityIcon(forecast.sunsetCategory)}{" "}
                {normalizeQualityCategory(forecast.sunsetCategory)}
              </Badge>
            </div>
            <div className="text-2xl font-bold text-center py-2">
              {formatQualityScore(forecast.sunsetQuality)}
            </div>
          </div>
        </div>

        {/* Weather Summary */}
        <div className="text-xs text-muted-foreground space-y-1 pt-2">
          <p>Cloud Cover: {forecast.cloudCover ?? "--"}%</p>
          <p>Humidity: {forecast.humidity ?? "--"}%</p>
          <p>Visibility: {forecast.visibility ? `${(forecast.visibility / 1000).toFixed(1)} km` : "--"}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function LocationForecastWrapper({
  location,
  date,
  onRemove,
  temperatureUnit,
  timeFormat,
  timezone,
  forecasts,
}: {
  location: ComparisonLocation;
  date: string;
  onRemove: () => void;
  temperatureUnit: "celsius" | "fahrenheit";
  timeFormat: "12h" | "24h";
  timezone: string;
  forecasts: (LocationForecast | null)[];
}) {
  const { forecast, isLoading, error } = useLocationForecast({
    latitude: location.latitude,
    longitude: location.longitude,
    date,
    locationId: location.id,
    timezone,
  });

  // Determine if this location has the best scores
  const allForecasts = [...forecasts, forecast].filter(Boolean) as LocationForecast[];
  const bestSunrise = allForecasts.length > 1 && forecast
    ? forecast.sunriseQuality === Math.max(...allForecasts.map(f => f.sunriseQuality))
    : undefined;
  const bestSunset = allForecasts.length > 1 && forecast
    ? forecast.sunsetQuality === Math.max(...allForecasts.map(f => f.sunsetQuality))
    : undefined;

  return (
    <ComparisonCard
      location={location}
      forecast={forecast ?? null}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      temperatureUnit={temperatureUnit}
      timeFormat={timeFormat}
      timezone={timezone}
      bestSunrise={bestSunrise}
      bestSunset={bestSunset}
    />
  );
}

export default function Compare() {
  const { isAuthenticated } = useAuth();
  const { locations, isLoading: locationsLoading } = useLocations();
  const { preferences } = usePreferences();
  const [selectedLocations, setSelectedLocations] = useState<ComparisonLocation[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const timezone = getUserTimezone();

  // Map API preference values to component-expected values
  const temperatureUnit: "celsius" | "fahrenheit" =
    preferences?.temperatureUnit === "CELSIUS" ? "celsius" : "fahrenheit";
  const timeFormat: "12h" | "24h" =
    preferences?.timeFormat === "TWENTY_FOUR_HOUR" ? "24h" : "12h";

  // Generate date options for the next 7 days
  const dateOptions = useMemo(() => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = addDays(today, i);
      options.push({
        value: format(date, "yyyy-MM-dd"),
        label: i === 0 ? "Today" : i === 1 ? "Tomorrow" : format(date, "EEE, MMM d"),
      });
    }
    return options;
  }, []);

  // Get available locations (not already selected)
  const availableLocations = useMemo(() => {
    const selectedIds = new Set(selectedLocations.map((l) => l.id));
    return locations.filter((loc) => !selectedIds.has(loc.id));
  }, [locations, selectedLocations]);

  const handleAddLocation = (locationId: string) => {
    const location = locations.find((l) => l.id === locationId);
    if (location && selectedLocations.length < MAX_LOCATIONS) {
      setSelectedLocations([
        ...selectedLocations,
        {
          id: location.id,
          name: location.name,
          latitude: location.latitude,
          longitude: location.longitude,
        },
      ]);
      setAddDialogOpen(false);
    }
  };

  const handleRemoveLocation = (locationId: string) => {
    setSelectedLocations(selectedLocations.filter((l) => l.id !== locationId));
  };

  // Not authenticated state
  if (!isAuthenticated) {
    return (
      <div className="container py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Compare Locations
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Sign in to compare forecasts across your saved locations.
            </p>
            <div className="flex gap-2 justify-center">
              <Link href="/login">
                <Button>Sign In</Button>
              </Link>
              <Link href="/signup">
                <Button variant="outline">Create Account</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No locations state
  if (!locationsLoading && locations.length === 0) {
    return (
      <div className="container py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Compare Locations
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <MapPin className="h-12 w-12 text-muted-foreground mx-auto" />
            <p className="text-muted-foreground">
              Add some locations first to compare their forecasts.
            </p>
            <Link href="/locations">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Locations
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-6 sm:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-5 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            Compare Locations
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Compare forecasts across up to {MAX_LOCATIONS} locations
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Date Selector */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedDate} onValueChange={setSelectedDate}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {dateOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Add Location Button */}
          {selectedLocations.length < MAX_LOCATIONS && (
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" disabled={availableLocations.length === 0}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Location
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Location to Compare</DialogTitle>
                  <DialogDescription>
                    Select a location to add to your comparison.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 mt-4 max-h-[300px] overflow-y-auto">
                  {availableLocations.map((location) => (
                    <Button
                      key={location.id}
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => handleAddLocation(location.id)}
                    >
                      <MapPin className="h-4 w-4 mr-2 text-primary" />
                      {location.name}
                    </Button>
                  ))}
                  {availableLocations.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">
                      All your locations are already being compared.
                    </p>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Comparison Grid */}
      {selectedLocations.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <MapPin className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Locations Selected</h2>
            <p className="text-muted-foreground mb-6">
              Add locations to compare their sunrise and sunset forecasts side by side.
            </p>
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Location
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Location to Compare</DialogTitle>
                  <DialogDescription>
                    Select a location to add to your comparison.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 mt-4 max-h-[300px] overflow-y-auto">
                  {locations.map((location) => (
                    <Button
                      key={location.id}
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => handleAddLocation(location.id)}
                    >
                      <MapPin className="h-4 w-4 mr-2 text-primary" />
                      {location.name}
                    </Button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      ) : (
        <div
          className={`grid gap-3 sm:gap-4 md:gap-6 ${
            selectedLocations.length === 1
              ? "grid-cols-1 max-w-md mx-auto"
              : selectedLocations.length === 2
              ? "grid-cols-1 md:grid-cols-2"
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {selectedLocations.map((location) => (
            <LocationForecastWrapper
              key={location.id}
              location={location}
              date={selectedDate}
              onRemove={() => handleRemoveLocation(location.id)}
              temperatureUnit={temperatureUnit}
              timeFormat={timeFormat}
              timezone={timezone}
              forecasts={[]} // We handle best detection inside the wrapper
            />
          ))}
        </div>
      )}

      {/* Legend */}
      {selectedLocations.length > 1 && (
        <div className="mt-6 flex items-center justify-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              <TrendingUp className="h-3 w-3 mr-1" />
              Best
            </Badge>
            <span>= Highest quality score</span>
          </div>
        </div>
      )}
    </div>
  );
}
